/**
 * AI Naming Settings Component
 * Issue #364: AI prompt customization UI for project-specific naming
 *
 * Allows users to customize the system prompt and guidelines
 * used for AI-powered interaction naming.
 */

import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '../../services/api';

interface AINamingSettingsProps {
  projectId: string;
  onClose: () => void;
}

interface PromptSettings {
  systemPrompt: string;
  guidelines: string | null;
}

// Default prompts (should match backend defaults)
const DEFAULT_SYSTEM_PROMPT = `You are a UX expert naming user interactions for a software application.
Generate concise, action-oriented names that describe what the user accomplishes, not the technical implementation.

Good examples:
- "Export Diagram as PNG" (not "Handle Export Click")
- "Create New Project" (not "Submit Form")
- "Show/Hide Legend" (not "Toggle Legend Visibility State")
- "Delete Selected Node" (not "Handle Delete")

Bad examples to avoid:
- Names starting with "Handle" or "On"
- Technical jargon like "setState", "dispatch", "emit"
- Generic names like "Click Button" or "Submit"
- Implementation details like "Call API" or "Update State"`;

const DEFAULT_GUIDELINES = `- Start with a verb (Create, Delete, Toggle, Export, Save, Open, Close, Show, Hide, etc.)
- Include the object being acted upon
- Be specific about the outcome when relevant
- Keep under 40 characters
- Use sentence case (not Title Case)
- Avoid articles (a, an, the) when possible
- For toggles, use "Show/Hide X" or "Toggle X" format`;

export function AINamingSettings({ projectId, onClose }: AINamingSettingsProps) {
  const [settings, setSettings] = useState<PromptSettings>({
    systemPrompt: DEFAULT_SYSTEM_PROMPT,
    guidelines: DEFAULT_GUIDELINES,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<PromptSettings | null>(null);

  // Load current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiFetch(`/projects/${projectId}/ai-naming-prompts`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          const loadedSettings = {
            systemPrompt: data.systemPrompt || DEFAULT_SYSTEM_PROMPT,
            guidelines: data.guidelines ?? DEFAULT_GUIDELINES,
          };
          setSettings(loadedSettings);
          setOriginalSettings(loadedSettings);
        } else if (response.status !== 404) {
          throw new Error('Failed to load settings');
        } else {
          // No custom settings yet, use defaults
          setOriginalSettings({
            systemPrompt: DEFAULT_SYSTEM_PROMPT,
            guidelines: DEFAULT_GUIDELINES,
          });
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        // Use defaults on error
        setOriginalSettings({
          systemPrompt: DEFAULT_SYSTEM_PROMPT,
          guidelines: DEFAULT_GUIDELINES,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [projectId]);

  // Track changes
  useEffect(() => {
    if (originalSettings) {
      const changed =
        settings.systemPrompt !== originalSettings.systemPrompt ||
        settings.guidelines !== originalSettings.guidelines;
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  // Save settings
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`/projects/${projectId}/ai-naming-prompts`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save settings');
      }

      setOriginalSettings(settings);
      setSuccess('Settings saved successfully');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  }, [projectId, settings]);

  // Reset to defaults
  const handleReset = useCallback(async () => {
    if (!confirm('Reset to default prompts? This will remove any customizations.')) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`/projects/${projectId}/ai-naming-prompts`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to reset settings');
      }

      const defaultSettings = {
        systemPrompt: DEFAULT_SYSTEM_PROMPT,
        guidelines: DEFAULT_GUIDELINES,
      };
      setSettings(defaultSettings);
      setOriginalSettings(defaultSettings);
      setSuccess('Reset to defaults');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings');
    } finally {
      setIsSaving(false);
    }
  }, [projectId]);

  // Cancel and close
  const handleCancel = useCallback(() => {
    if (hasChanges && !confirm('Discard unsaved changes?')) {
      return;
    }
    onClose();
  }, [hasChanges, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚙️</span>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              AI Naming Settings
            </h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
            </div>
          ) : (
            <>
              {/* Info */}
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  Customize how AI generates names for user interactions in this project.
                  These settings apply to both individual and bulk AI naming.
                </p>
              </div>

              {/* System Prompt */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  System Prompt
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  Defines the AI's role and approach to naming. Include examples of good and bad names.
                </p>
                <textarea
                  value={settings.systemPrompt}
                  onChange={(e) => setSettings(prev => ({ ...prev, systemPrompt: e.target.value }))}
                  rows={10}
                  className="w-full px-3 py-2 text-sm font-mono border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter system prompt..."
                />
              </div>

              {/* Guidelines */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Naming Guidelines
                </label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  Specific rules for name formatting. These are appended to the system prompt.
                </p>
                <textarea
                  value={settings.guidelines || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, guidelines: e.target.value || null }))}
                  rows={8}
                  className="w-full px-3 py-2 text-sm font-mono border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter guidelines..."
                />
              </div>

              {/* Tips */}
              <div className="p-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Tips</h3>
                <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
                  <li>Include domain-specific terminology in your examples</li>
                  <li>The AI learns from your feedback on suggested names</li>
                  <li>Approved and edited names become few-shot examples</li>
                  <li>Reset to defaults if names become inconsistent</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={handleReset}
            disabled={isSaving}
            className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 disabled:opacity-50"
          >
            Reset to Defaults
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AINamingSettings;
